"use client";

import { cloneElement, isValidElement, useId } from "react";
import { Check, AlertCircle } from "lucide-react";
import { cn } from "../../lib/cn";

/** Shared invalid-field treatment: red border + soft red ring so an
 *  unfilled required field reads as clearly "framed" / needs attention. */
export const INVALID_FIELD = "!border-danger-500 !ring-2 !ring-danger-500/25";

/** Shared field-error line: an alert icon + readable message. Bigger and
 *  clearer than a bare 11px line so a refused field reads at a glance. Used by
 *  FloatField and the custom pickers (PlaceInput, StopRow, hero) for one
 *  consistent error treatment. */
export function FieldError({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <p
      role="alert"
      className={cn(
        "mt-1.5 flex items-start gap-1.5 text-[13px] font-medium leading-snug text-danger-500",
        className,
      )}
    >
      <AlertCircle aria-hidden strokeWidth={2.5} className="mt-px h-3.5 w-3.5 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

/** Shared valid-checkmark — green check, used inside FloatField and the
 *  custom pickers (PhoneInput, PlaceInput, selects) for live positive
 *  feedback. Icon only, border stays neutral (calm design). */
export function ValidCheck({ className }: { className?: string }) {
  return (
    <Check
      aria-hidden
      strokeWidth={3}
      className={cn("h-4 w-4 shrink-0 text-green-600", className)}
    />
  );
}

/**
 * Floating-label input shell for the booking forms: the label sits on the
 * top border of the field, an optional unit suffix sits inside on the right.
 * Pass the `<input>` (already wired to react-hook-form) as `children`.
 * `valid` renders a green check inside the field (left of the suffix when
 * one exists) — live positive feedback once the value passes validation.
 */
export function FloatField({
  label,
  required = false,
  suffix,
  error,
  valid = false,
  className,
  controlId,
  children,
}: {
  label: string;
  required?: boolean;
  suffix?: string;
  error?: string;
  valid?: boolean;
  className?: string;
  /** Id des eigentlichen Felds, wenn es nicht das direkte Kind ist (z. B.
   *  ein <select> in einem Wrapper mit Icon). Sonst zeigte das Label auf den
   *  Wrapper, und ein Tipp darauf blieb tot (Clarity 06.10.2026, Verpackung). */
  controlId?: string;
  children: React.ReactNode;
}) {
  // Associate the floating label with its input so tapping the label focuses
  // the field. A bare <span> label (pointer-events-none, no htmlFor) read as a
  // dead click in Clarity: users tap a field label expecting focus and nothing
  // happened. Mint an id, render a real <label htmlFor>, and inject the id onto
  // the child input (preserving any id the caller already set).
  const autoId = useId();
  let control = children;
  let inputId = controlId ?? autoId;
  if (!controlId && isValidElement(children)) {
    const childId = (children.props as { id?: string }).id;
    inputId = childId ?? autoId;
    if (!childId) {
      control = cloneElement(children as React.ReactElement<{ id?: string }>, {
        id: inputId,
      });
    }
  }
  return (
    <div className={className}>
      <div className="relative">
        <label
          htmlFor={inputId}
          className={cn(
            "absolute -top-2 left-2.5 z-10 cursor-text bg-white px-1 text-[11px] font-medium",
            error ? "text-danger-500" : "text-ink-700",
          )}
        >
          {label}
          {required ? (
            <span className={error ? "text-danger-500" : "text-brand-700"}> *</span>
          ) : null}
        </label>
        {control}
        {suffix ? (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[12px] font-medium text-ink-400">
            {suffix}
          </span>
        ) : null}
        {valid && !error ? (
          <span
            className={cn(
              "pointer-events-none absolute top-1/2 -translate-y-1/2",
              suffix ? "right-9" : "right-2",
            )}
          >
            {/* Compact 14px check — narrow fields (PLZ at ~6rem) must keep
                all five digits readable next to it. */}
            <ValidCheck className="h-3.5 w-3.5" />
          </span>
        ) : null}
      </div>
      {error ? <FieldError>{error}</FieldError> : null}
    </div>
  );
}

/** Class for an input used inside a FloatField. `hasSuffix`/`valid` reserve
 *  room on the right so the value never collides with the unit / check. */
export function floatInputClass(opts?: {
  hasSuffix?: boolean;
  invalid?: boolean;
  valid?: boolean;
}): string {
  return cn(
    "input tabnums h-11",
    opts?.hasSuffix && !opts?.valid && "pr-9",
    opts?.hasSuffix && opts?.valid && "pr-14",
    !opts?.hasSuffix && opts?.valid && "pr-7",
    opts?.invalid && INVALID_FIELD,
  );
}

/** True when the react-hook-form error tree has an entry at `path`
 *  (dot-separated, e.g. "stops.0" or "sender.zip"). */
export function errorAtPath(errors: unknown, path: string): boolean {
  let cursor: unknown = errors;
  for (const p of path.split(".")) {
    if (cursor && typeof cursor === "object" && p in (cursor as Record<string, unknown>)) {
      cursor = (cursor as Record<string, unknown>)[p];
    } else {
      return false;
    }
  }
  return Boolean(cursor);
}

/** Same walk over react-hook-form's `formState.touchedFields`. */
export function touchedAtPath(touched: unknown, path: string): boolean {
  return errorAtPath(touched, path);
}

export type FieldVisualState = "neutral" | "invalid" | "valid";

/**
 * Unified live field state for the booking form.
 *
 * Computed from the VALUE, not only from the Zod error tree — required-ness
 * for contact/identity lives in a top-level `superRefine`, and Zod skips
 * refinements entirely while any base field error exists. Relying on errors
 * alone is why a failed Weiter used to paint only the street red while the
 * equally-empty email/phone stayed neutral.
 *
 *   empty:  required && (touched || attempted) → invalid, else neutral
 *   filled: hasError → invalid, else valid (green check)
 *
 * `attempted` = the user clicked Weiter on this step and was refused
 * (see FieldFeedbackContext in booking-flow-v2).
 */
export function fieldState(opts: {
  value: unknown;
  hasError: boolean;
  required: boolean;
  touched: boolean;
  attempted: boolean;
}): FieldVisualState {
  const empty =
    opts.value == null ||
    (typeof opts.value === "string" && opts.value.trim() === "");
  if (empty) {
    return opts.required && (opts.touched || opts.attempted) ? "invalid" : "neutral";
  }
  return opts.hasError ? "invalid" : "valid";
}
